import Select, { type SingleValue } from 'react-select';
import { getAllServices } from '../../sources/service';
import { useState, useEffect } from 'react';
import type { ServiceElementResponseAPI } from '../../types/service';
import { getSharedStyles } from '../../utils/select';

interface ConnectorDropdownProps {
    selectedConnectorId: string | null;
    onChange: (selection: SingleValue<{ value: string; label: string | null }>) => void;
    onError: (message: unknown) => void;
    isOpenApi: (isOpenApi: boolean) => void;
}

const ConnectorDropdown = ({
    selectedConnectorId,
    onChange,
    onError,
    isOpenApi,
}: ConnectorDropdownProps) => {
    const [connectors, setConnectors] = useState<ServiceElementResponseAPI[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchConnectors = async () => {
        try {
            const connectorsResults: ServiceElementResponseAPI[] = await getAllServices();

            setConnectors(connectorsResults.filter(({ providesFiles }) => providesFiles));
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const findSelectedConnector = (connectorId: string | null | undefined) => {
        return connectors.find((connector) => connector.id === connectorId);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        fetchConnectors();
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: if you add findSelectedConnector it results in a qa error
    useEffect(() => {
        const connector = findSelectedConnector(selectedConnectorId);
        isOpenApi(connector?.uri === 'flow://openapi');
    }, [selectedConnectorId, isOpenApi, connectors]);

    const connectorOptions = connectors.map((connector) => ({
        value: connector.id,
        label: connector.developerName,
    }));

    const selectedConnector = findSelectedConnector(selectedConnectorId);

    return (
        <>
            {isLoading ? (
                'Loading connectors'
            ) : (
                <Select
                    styles={getSharedStyles<(typeof connectorOptions)[number]>()}
                    inputId="connector-dropdown"
                    name="connector-dropdown"
                    isMulti={false}
                    options={connectorOptions}
                    onChange={onChange}
                    placeholder="Select a connector"
                    value={
                        selectedConnector
                            ? {
                                  value: selectedConnector.id,
                                  label: selectedConnector.developerName,
                              }
                            : null
                    }
                    noOptionsMessage={() => 'No results found'}
                    isClearable
                />
            )}
        </>
    );
};

export default ConnectorDropdown;
